import { type ReactNode } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { PortfolioSortKeySchema, PortfolioSortDirectionSchema } from '@repo/shared/common';
import {
  ensureRouteData,
  ensureRouteInfiniteData,
  modelingStatusQueryOptions,
} from '~/lib/query';
import {
  portfolioSummaryQueryOptions,
  portfolioLoansInfiniteQueryOptions,
} from '~/features/portfolio/use-portfolio';
import {
  segmentFieldsQueryOptions,
  segmentDraftQueryOptions,
} from '~/features/portfolio/use-segment';
import { PortfolioPage } from '~/features/portfolio/PortfolioPageConnected';
import { DEFAULT_PORTFOLIO_VIEW, type PortfolioViewState } from '~/features/portfolio/PortfolioPage';

// The URL carries only navigational state: loan-id search + sort (ADHERENCE:
// search/sort state is shareable). The SEGMENT (field/readiness slicing) lives in the
// per-user store + server draft, not the URL — so a shared link reproduces the table's
// search/sort, and the viewer's own saved segment applies on top. Every field `.catch`es
// to undefined so a malformed query degrades to the default view.
const searchSchema = z.object({
  search: z.string().optional().catch(undefined),
  sort: PortfolioSortKeySchema.optional().catch(undefined),
  dir: PortfolioSortDirectionSchema.optional().catch(undefined),
});
type PortfolioSearch = z.infer<typeof searchSchema>;

function searchToState(search: PortfolioSearch): PortfolioViewState {
  return {
    search: search.search ?? DEFAULT_PORTFOLIO_VIEW.search,
    sort: search.sort ?? DEFAULT_PORTFOLIO_VIEW.sort,
    direction: search.dir ?? DEFAULT_PORTFOLIO_VIEW.direction,
  };
}

/** Serialize state to search, dropping default-valued fields so the URL stays clean. */
function stateToSearch(state: PortfolioViewState): PortfolioSearch {
  return {
    search: state.search.trim() !== '' ? state.search : undefined,
    sort: state.sort !== DEFAULT_PORTFOLIO_VIEW.sort ? state.sort : undefined,
    dir: state.direction !== DEFAULT_PORTFOLIO_VIEW.direction ? state.direction : undefined,
  };
}

export const Route = createFileRoute('/_protected/portfolio')({
  validateSearch: (search: Record<string, unknown>): PortfolioSearch => searchSchema.parse(search),
  loaderDeps: ({ search }): PortfolioSearch => search,
  loader: async ({ context, deps }) => {
    // Page-defining data: await on the server (flash-free first paint), prefetch on the
    // client. The block-until-settled status is awaited so the first paint already knows
    // whether to show the "still processing" block or the portfolio (no flash between them,
    // and the same signal the Modeling screen blocks on). The loans first page tracks the URL
    // search/sort (NOT the segment — that's a client store that hydrates after paint, then
    // re-fetches the filtered page); the segment fields + the user's draft warm the panel so
    // it paints filled.
    await Promise.all([
      ensureRouteData(context.queryClient, modelingStatusQueryOptions()),
      ensureRouteData(context.queryClient, portfolioSummaryQueryOptions()),
      ensureRouteData(context.queryClient, segmentFieldsQueryOptions()),
      ensureRouteData(context.queryClient, segmentDraftQueryOptions()),
      ensureRouteInfiniteData(
        context.queryClient,
        portfolioLoansInfiniteQueryOptions(searchToState(deps)),
      ),
    ]);
  },
  component: PortfolioRoute,
});

function PortfolioRoute(): ReactNode {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const state = searchToState(search);

  const onChange = (patch: Partial<PortfolioViewState>): void => {
    // Search/sort are in-page refinements of the same list, not a navigation to a
    // new screen — keep the scroll position (resetScroll:false) so refining the
    // view doesn't snap the user back to the top.
    void navigate({
      search: stateToSearch({ ...state, ...patch }),
      replace: true,
      resetScroll: false,
    });
  };

  return <PortfolioPage state={state} onChange={onChange} />;
}
